<template>
    <VJoyWrapper column-gap="0" row-gap="3" item-stretch>
        <VJoyWrapper item-stretch no-margin align="flex-start">
            <VJoyWrapper align="stretch" direction="column" no-margin row-gap="2">
                <VJoyInput
                    v-model.trim="r$.$value.firstname"
                    data-testid="account-creation-form-firstname"
                    type="text"
                    :label="$t('profile.features.account.form.firstname.label')"
                    :placeholder="$t('profile.features.account.form.firstname.placeholder')"
                    name="firstname"
                    :invalid="r$.firstname.$error"
                />
                <VJoyFormError
                    v-if="r$.firstname.$error"
                    :errors="r$.firstname.$errors"
                    class="account-password-creation-form-firstname-error"
                    data-testid="account-password-creation-form-firstname-error"
                    no-margin
                />
            </VJoyWrapper>
            <VJoyWrapper align="stretch" direction="column" no-margin row-gap="2">
                <VJoyInput
                    v-model.trim="r$.$value.lastname"
                    data-testid="account-creation-form-lastname"
                    type="text"
                    :label="$t('profile.features.account.form.lastname.label')"
                    :placeholder="$t('profile.features.account.form.lastname.placeholder')"
                    name="lastname"
                    :invalid="r$.lastname.$error"
                />
                <VJoyFormError
                    v-if="r$.lastname.$error"
                    :errors="r$.lastname.$errors"
                    class="account-password-creation-form-lastname-error"
                    data-testid="account-password-creation-form-lastname-error"
                    no-margin
                />
            </VJoyWrapper>
        </VJoyWrapper>

        <VJoyWrapper item-stretch class="account-creation-form__password">
            <MaltInputPassword
                v-model="r$.$value.password"
                :field="r$.password"
                data-testid="signup-password-input"
                :label="$t('profile.features.account.form.password.label')"
            />
        </VJoyWrapper>
        <VJoyWrapper item-stretch no-margin>
            <VJoyCheckbox
                v-model:checked="r$.$value.isCheckedMaltsTermsAndConditions"
                name="malts-terms-and-conditions"
                class="account-creation-form-terms-and-conditions"
                data-testid="account-creation-form-malt-terms-and-conditions"
            >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="$t('profile.features.account.form.terms.and.conditions.label')" />
            </VJoyCheckbox>
        </VJoyWrapper>
        <VJoyWrapper item-stretch no-margin>
            <VJoyCheckbox
                v-model:checked="r$.$value.isCheckedMaltsPrivacyPolicy"
                name="malts-privacy-policy"
                class="account-creation-form-terms-and-conditions"
                data-testid="account-creation-form-malt-privacy-policy"
            >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="$t('profile.features.account.form.privacy.policy.label')" />
            </VJoyCheckbox>
        </VJoyWrapper>
    </VJoyWrapper>
</template>
<script setup lang="ts">
    import {reactive, watch} from 'vue';
    import type {RegleExternalErrorTree} from '@regle/core';
    import {checked, maxLength, minLength, required} from '@regle/rules';
    import {MaltInputPassword} from '#components';
    import {useDebounce} from '#imports';
    import {useRegle} from '@malt/forms';
    import {VJoyCheckbox, VJoyFormError, VJoyInput, VJoyWrapper} from '@maltjoy/core-vue';
    import type {AccountCreationFormData, AccountPasswordCreationFormData} from '~/features/account/types/AccountCreationFormData';

    type AccountPasswordCreationFormProps = {
        firstname?: string;
        lastname?: string;
        password?: string;
        isCheckedMaltsPrivacyPolicy?: boolean;
        isCheckedMaltsTermsAndConditions?: boolean;
    };

    const props = defineProps<AccountPasswordCreationFormProps>();
    const emit = defineEmits<{
        (e: 'account-creation-form:update', value: {isFormValid: boolean; form: AccountCreationFormData}): void;
    }>();

    const externalErrors = defineModel<RegleExternalErrorTree<AccountPasswordCreationFormData>>('externalErrors', {required: true});

    const {debounce} = useDebounce();

    const form = reactive<AccountPasswordCreationFormData>({
        firstname: props.firstname,
        lastname: props.lastname,
        password: props.password,
        isCheckedMaltsTermsAndConditions: props.isCheckedMaltsTermsAndConditions ?? false,
        isCheckedMaltsPrivacyPolicy: props.isCheckedMaltsPrivacyPolicy ?? false,
    });

    const {r$} = useRegle(
        form,
        {
            firstname: {required, minLength: minLength(1), maxLength: maxLength(100)},
            lastname: {required, minLength: minLength(1), maxLength: maxLength(100)},
            password: {required},
            isCheckedMaltsTermsAndConditions: {checked},
            isCheckedMaltsPrivacyPolicy: {checked},
        },
        {externalErrors},
    );

    watch(
        [() => r$.$invalid, form],
        () => {
            debounce(() => {
                fieldUpdated();
            }, 200);
        },
        {deep: true},
    );

    function fieldUpdated() {
        emit('account-creation-form:update', {isFormValid: !r$.$invalid, form});
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .account-creation-form {
        &__password {
            width: 100%;
        }

        &-username-error,
        &-terms-and-conditions {
            &,
            a {
                @include text.getFontBySize(primary-400);
            }
        }
    }
</style>
